import { Suspense } from 'react';
import { useParams } from 'react-router';
import { UsageDetail } from '@gonfalon/billing/components';
import { AggregationType, type UsageTabKey } from '@gonfalon/billing/types';

import { Card, ErrorCallout } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { useSettings } from '../../shared/hooks/useSettings';
import { useAccount } from '../hooks/useAccount';
import { useAccountSubscription } from '../hooks/useAccountSubscription';

import { AccountContextProvider } from './AccountContextProvider';
import { AccountHeading } from './AccountHeading';

import './DiagnosticUsageDetailsPage.scss';

export function DiagnosticUsageDetailsPage() {
  const { accountId, metricType } = useParams<{ accountId: string; metricType: string }>();

  // Fetch account data. Hooks must run unconditionally, so these run before
  // the accountId/metricType guard below; useAccount disables its query
  // when accountId is empty, and useAccountSubscription already accepts
  // null/undefined.
  const accountQuery = useAccount(accountId ?? '');
  const account = accountId ? accountQuery.data?.entities?.accounts?.[accountId] : undefined;
  const { data: subscription, isLoading, error } = useAccountSubscription(account || null);
  const { vitalstatistixDataExportObservability } = useSettings();

  if (!accountId || !metricType) {
    return (
      <div>
        <ErrorCallout>
          <strong>Invalid URL</strong>
          <p>Missing account ID or metric type in the URL.</p>
        </ErrorCallout>
      </div>
    );
  }

  if (accountQuery.isLoading) {
    return (
      <div className="DiagnosticUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading account data...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (accountQuery.error || !account) {
    return (
      <div className="DiagnosticUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load account data</strong>
                <p>Account not found or could not be loaded.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="DiagnosticUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading diagnostic usage details...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (error) {
    return (
      <div className="DiagnosticUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load diagnostic usage data</strong>
                <p>Diagnostic usage data could not be loaded. Please try refreshing the page.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  return (
    <div className="DiagnosticUsageDetailsPage">
      <AccountHeading account={account} />
      <Flex>
        <Box width={1} p={2}>
          <Card>
            <Suspense fallback={<div>Loading diagnostic usage details...</div>}>
              <AccountContextProvider
                key={String(vitalstatistixDataExportObservability)}
                account={account}
                subscription={subscription || undefined}
              >
                <UsageDetail
                  usageTab={metricType as UsageTabKey}
                  defaultAggregationType={AggregationType.INCREMENTAL}
                />
              </AccountContextProvider>
            </Suspense>
          </Card>
        </Box>
      </Flex>
    </div>
  );
}
